import type { QueryClient } from "@tanstack/react-query";
import { EventIdPublic } from "@shared/schemas/branded-id.schemas";
import {
  OpenInvitationEventOwnerViewSchema,
  type OpenInvitation,
  type OpenInvitationEventOwnerView,
} from "@shared/schemas/open-invitation.schemas";
import type {
  CreateOpenInvitationInputType,
  DisableOpenInvitationInputType,
  EditDefaultInvitationPhraseInputType,
  CreateMagicLinksInputType,
} from "./open-invitation.types";
import { BaseApiHooksRepository } from "../../base-api-hooks.repository";
import { createOpenInvitationsApi } from "./open-invitations.api";
import type { AxiosInstance } from "axios";
import type { OpenInvitationWithMagicLinks } from "@shared/schemas/open-invitation-with-magic-links.schemas";

const QUERY_KEY_PREFIX = "open-invitations";

export const openInvitationsQueryKeys = {
  all: [QUERY_KEY_PREFIX] as const,
  byEvent: (eventIdPublic: EventIdPublic): readonly [string, string, string] =>
    [QUERY_KEY_PREFIX, "event", EventIdPublic.unwrap(eventIdPublic)] as const,
  byEventResolved: (
    eventIdPublic: EventIdPublic,
  ): readonly [string, string, string, string] =>
    [
      QUERY_KEY_PREFIX,
      "event",
      EventIdPublic.unwrap(eventIdPublic),
      "resolved",
    ] as const,
};

export class OpenInvitationHooksHttpRepositoryImpl extends BaseApiHooksRepository {
  private readonly api;

  constructor(queryClient: QueryClient, apiClient: AxiosInstance) {
    super(queryClient);
    this.api = createOpenInvitationsApi(apiClient);
  }

  createHooks() {
    return {
      useGetByEventIdPublic: (eventIdPublic: EventIdPublic) => {
        return this.query({
          key: openInvitationsQueryKeys.byEvent(eventIdPublic),
          fn: () => this.api.getByEvent(eventIdPublic),
          parse: (dtos) =>
            dtos.map((dto) => OpenInvitationEventOwnerViewSchema.parse(dto)),
          enabled: EventIdPublic.unwrap(eventIdPublic) !== "",
        });
      },

      useGetByEventIdPublicResolved: (eventIdPublic: EventIdPublic) => {
        return this.query({
          key: openInvitationsQueryKeys.byEventResolved(eventIdPublic),
          fn: () => this.api.getByEventResolved(eventIdPublic),
          parse: (dtos): readonly OpenInvitationWithMagicLinks[] =>
            dtos.map((dto) => ({
              invitation: OpenInvitationEventOwnerViewSchema.parse(
                dto.invitation,
              ),
              magicLinks: dto.magicLinks,
            })),
          enabled: EventIdPublic.unwrap(eventIdPublic) !== "",
        });
      },

      useCreate: () => {
        return this.mutation<
          OpenInvitationEventOwnerView,
          OpenInvitation,
          CreateOpenInvitationInputType
        >({
          fn: (input) =>
            this.api.create({
              eventIdPublic: input.eventIdPublic,
              phrase: input.phrase,
              label: input.label,
              maxParticipants: input.maxParticipants ?? null,
              expiresAt:
                input.expiresAt !== undefined && input.expiresAt !== null
                  ? input.expiresAt.toISOString()
                  : null,
            }),
          parse: (dto) => OpenInvitationEventOwnerViewSchema.parse(dto),
          onSuccess: (_data, variables) => {
            this.invalidateQueries(
              openInvitationsQueryKeys.byEvent(variables.eventIdPublic),
            );
          },
        });
      },

      useDisable: () => {
        return this.mutation<
          OpenInvitationEventOwnerView,
          OpenInvitation,
          DisableOpenInvitationInputType
        >({
          fn: (input) => this.api.disable(input.eventIdPublic, input.idPublic),
          parse: (dto) => OpenInvitationEventOwnerViewSchema.parse(dto),
          onSuccess: (_data, variables) => {
            this.invalidateQueries(
              openInvitationsQueryKeys.byEvent(variables.eventIdPublic),
            );
          },
        });
      },

      useEditDefaultPhrase: () => {
        return this.mutation<
          OpenInvitationEventOwnerView,
          OpenInvitation,
          EditDefaultInvitationPhraseInputType
        >({
          fn: (input) =>
            this.api.editDefaultPhrase(input.eventIdPublic, input.idPublic, {
              phrase: input.phrase,
            }),
          parse: (dto) => OpenInvitationEventOwnerViewSchema.parse(dto),
          onSuccess: (_data, variables) => {
            this.invalidateMultipleQueries([
              openInvitationsQueryKeys.byEvent(variables.eventIdPublic),
              openInvitationsQueryKeys.byEventResolved(variables.eventIdPublic),
            ]);
          },
        });
      },

      useCreateMagicLinks: () => {
        return this.mutation<
          OpenInvitationWithMagicLinks,
          OpenInvitationWithMagicLinks,
          CreateMagicLinksInputType
        >({
          fn: (input) =>
            this.api.createMagicLinks(input.eventIdPublic, input.idPublic, {
              channels: [...input.channels],
            }),
          parse: (response) => ({
            invitation: OpenInvitationEventOwnerViewSchema.parse(
              response.invitation,
            ),
            magicLinks: response.magicLinks,
          }),
          onSuccess: (_data, variables) => {
            this.invalidateMultipleQueries([
              openInvitationsQueryKeys.byEvent(variables.eventIdPublic),
              openInvitationsQueryKeys.byEventResolved(variables.eventIdPublic),
            ]);
          },
        });
      },
    };
  }
}
